import { useCallback, useEffect, useRef } from "react";

/**
 * Runs the function at most once every `delay` milliseconds.
 * If the function is called again before the delay has passed, it will not execute until the next time the delay has passed.
 * If the function is called again after the delay has passed, it will execute immediately.
 */
export function useThrottledFunction<Args extends any[], Return>(
  fn: (...args: Args) => Return,
  delay: number,
): (...args: Args) => void {
  const lastCallTimeRef = useRef<number>(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingArgsRef = useRef<Args | null>(null);

  const throttledFn = useCallback(
    (...args: Args) => {
      const now = Date.now();
      const timeSinceLastCall = now - lastCallTimeRef.current;

      const executeFunction = (argsToUse: Args) => {
        fn(...argsToUse);
        lastCallTimeRef.current = Date.now();
      };

      if (timeSinceLastCall >= delay) {
        // Execute immediately if enough time has passed
        executeFunction(args);

        // Clear any pending execution
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
          pendingArgsRef.current = null;
        }
      } else {
        // Store the args for the next execution
        pendingArgsRef.current = args;

        // Schedule the next execution if not already scheduled
        if (!timeoutRef.current) {
          const remainingDelay = delay - timeSinceLastCall;
          timeoutRef.current = setTimeout(() => {
            if (pendingArgsRef.current) {
              executeFunction(pendingArgsRef.current);
            }
            timeoutRef.current = null;
            pendingArgsRef.current = null;
          }, remainingDelay);
        }
      }
    },
    [fn, delay],
  );

  return throttledFn;
}

type Pending<R> = Array<{
  resolve: (value: R) => void;
  reject: (error: any) => void;
}>;

const DEFAULT_KEY_FN = () => "__default__";

/**
 * Debounces an async function while preserving its return type.
 * Only the most recent call within the debounce window will be executed and resolved.
 * Earlier calls will never resolve.
 *
 * @param fn The async function to debounce
 * @param delay The delay in milliseconds
 * @param keyFn Optional function to generate a unique key for each call based on the arguments - useful when multiple calls are being made in parallel and should be debounced separately.
 * @returns A debounced version of the input function with the same signature
 */
export function useAsyncDebounce<Args extends unknown[], R>(
  fn: (...args: Args) => Promise<R>,
  delay: number,
  keyFn: (...args: Args) => string = DEFAULT_KEY_FN,
): (...args: Args) => Promise<R> {
  const mapRef = useRef<
    Map<
      string,
      {
        args: Args;
        timer: ReturnType<typeof setTimeout> | null;
        pending: Pending<R>;
      }
    >
  >(new Map());

  useEffect(() => {
    const map = mapRef.current;
    return () => {
      for (const { timer, pending } of map.values()) {
        if (timer) {
          clearTimeout(timer);
        }
        for (const { reject } of pending)
          reject(new Error("Debounced function was unmounted"));
      }
      map.clear();
    };
  }, []);

  return useCallback(
    (...args: Args): Promise<R> => {
      const key = keyFn(...args);

      let entry = mapRef.current.get(key);
      if (entry && entry.timer) {
        clearTimeout(entry.timer);
      } else {
        entry = { args, timer: null, pending: [] };
        mapRef.current.set(key, entry);
      }

      entry.args = args;

      const promise = new Promise<R>((resolve, reject) => {
        entry.pending.push({ resolve, reject });
      });

      entry.timer = setTimeout(async () => {
        mapRef.current.delete(key);

        try {
          const result = await fn(...entry!.args);
          for (const { resolve } of entry.pending) resolve(result);
        } catch (err) {
          for (const { reject } of entry.pending) reject(err);
        }
      }, delay);

      return promise;
    },
    [fn, delay, keyFn],
  );
}

export { useDebounce } from "usehooks-ts";
